import * as ElectronSentry from '@sentry/electron/renderer'
import * as ReactSentry from '@sentry/react'
import { EnrollmentStatusResponse } from '../domains/enrollment/types'
import { SystemNotification } from '../domains/types'
import { AuthenticatedUser } from '../modules/authentication/types'
import { TRACE_PROPAGATION_PATTERNS } from './trace-targets'

const DEV_SENTRY_DSN =
  'https://ca937f96b418a88885ada0ae45b5702f@o241876.ingest.us.sentry.io/4510250829479936'

// Detect Electron renderer at runtime
function isElectronRenderer(): boolean {
  return (
    typeof navigator !== 'undefined' && navigator.userAgent.includes('Electron')
  )
}

let sentrySetup = false

// Cypress serves the app under test from a production build, so the build-time
// PROD flag alone would enable Sentry with the real DSN and stream deliberately
// stubbed network failures from e2e specs into the production projects
// (e.g. CODING-UI-4G). window.Cypress exists in the app before any app code runs.
// Module-private, matching isElectronRenderer above.
function isRunningUnderCypress(): boolean {
  return typeof window !== 'undefined' && 'Cypress' in window
}

export async function initSentry(version: string, dsn: string) {
  if (isRunningUnderCypress()) {
    console.log('Sentry disabled: Cypress test run detected')

    return
  }

  const isDev = import.meta.env.DEV
  const isProd = import.meta.env.PROD

  const enabled = isProd
    ? true
    : // optional opt-in for local/dev runs
      import.meta.env.SENTRY_IN_DEV === '1' ||
      localStorage.getItem('SENTRY_IN_DEV') === '1'

  if (!enabled) {
    console.log(
      'Sentry disabled, if needed in development set SENTRY_IN_DEV=1 in localStorage or as an env var'
    )
    return
  }

  console.log(`Initializing Sentry for environment: ${import.meta.env.MODE}`)

  // Keep this tiny in dev; 0.2 = 20% of error events
  const ERROR_SAMPLE_RATE = isDev ? 0.2 : 1.0

  // Near-zero transaction volume in dev
  const TRACES_SAMPLE_RATE = isDev ? 0.02 : 0.1

  // Replay: off for sessions; small on-error sample
  const REPLAY_SESSION_RATE = isDev ? 0.0 : 0.1
  const REPLAY_ON_ERROR_RATE = isDev ? 0.1 : 1.0

  const sentryUrls = [...TRACE_PROPAGATION_PATTERNS]

  const sentryConfig: ReactSentry.BrowserOptions = {
    // Use different DSN for dev vs staging or prod //
    // This is to avoid polluting the production sentry projects //
    dsn: isDev ? DEV_SENTRY_DSN : dsn,

    // debug: true,
    transport: ReactSentry.makeBrowserOfflineTransport?.(
      ReactSentry.makeFetchTransport
    ),
    integrations: [
      ReactSentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        networkCaptureBodies: true,
        networkDetailAllowUrls: [window.location.origin, ...sentryUrls],

        networkRequestHeaders: ['Baggage']
      }),
      ReactSentry.feedbackIntegration({
        colorScheme: 'system',
        showBranding: false,
        autoInject: false
      }),
      ReactSentry.httpClientIntegration(),
      ReactSentry.replayCanvasIntegration(),
      ReactSentry.browserTracingIntegration()

      // Optional profiling:
      // ReactSentry.browserProfilingIntegration(),
    ],
    release: version,
    environment: import.meta.env.MODE,
    tracePropagationTargets: sentryUrls,

    sampleRate: ERROR_SAMPLE_RATE, // error event sampling

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: TRACES_SAMPLE_RATE,

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: REPLAY_SESSION_RATE,
    replaysOnErrorSampleRate: REPLAY_ON_ERROR_RATE
  }

  if (isElectronRenderer()) {
    // Initialize Sentry for Electron renderer process and combine with React SDK //
    console.log('Initializing Sentry for Electron/React', sentryConfig.dsn)
    ElectronSentry.init(sentryConfig, ReactSentry.init)
  } else {
    console.log('Initializing Sentry for Web/React', sentryConfig.dsn)
    ReactSentry.init(sentryConfig)
  }

  sentrySetup = true
}

export function isSentrySetup() {
  return sentrySetup
}

export const openSentryBugReporting = async () => {
  const feedback = ReactSentry.getFeedback()

  if (!feedback) return

  try {
    const form = await feedback.createForm({
      colorScheme: 'light',
      showEmail: false,
      showName: false,
      showBranding: false,
      useSentryUser: {
        name: 'fullName',
        email: 'email'
      }
    })

    form.appendToDom()

    // Position form in bottom-left corner
    const style = document.createElement('style')
    style.textContent = `
      #sentry-feedback {
        --inset: auto auto 0 0 !important;
        --page-margin: 20px !important;
        --box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3) !important;
        --z-index: 10000 !important;
      }
    `
    document.head.appendChild(style)

    form.open()
  } catch {
    console.error('Failed to open Sentry feedback form')
  }
}

export const updateSentryUser = (
  data: AuthenticatedUser | EnrollmentStatusResponse['laptop'] | null
) => {
  if (!data) {
    ReactSentry.setUser(null)
    return
  }
  // Determine if data is AuthenticatedUser or Laptop based on presence of 'email' and 'firstName' //
  if ('email' in data && 'firstName' in data) {
    ReactSentry.setUser({
      email: data.email,
      id: data.email, // Use email as ID since AuthenticatedUser doesn't have id field
      username: `${data.firstName} ${data.lastName}`
    })
  } else {
    ReactSentry.setUser({
      id: data.name,
      username: data.organisation?.name,
      email: data.name
    })
  }
}

export const updateLaptopContext = (
  laptop: EnrollmentStatusResponse['laptop'],
  msState: SystemNotification
) => {
  ReactSentry.setContext('laptop_meta', {
    organisation: laptop?.organisation?.name ?? 'Unknown',
    region: laptop?.region ?? 'Unknown',
    environment: laptop?.environment ?? 'Unknown',
    ...(msState || { msState: 'MS State Unknown' })
  })
}
